import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Building2, 
  MapPin, 
  Link as LinkIcon, 
  ArrowRight, 
  Loader2,
  Utensils,
  Stethoscope,
  Pill,
  ShoppingBag,
  Scissors,
  Car,
  Building,
  MoreHorizontal
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const businessTypes = [
  { value: "restaurant", label: "Restaurant", icon: Utensils },
  { value: "medical", label: "Cabinet médical", icon: Stethoscope },
  { value: "pharmacy", label: "Pharmacie", icon: Pill },
  { value: "retail", label: "Commerce", icon: ShoppingBag },
  { value: "beauty", label: "Salon de beauté", icon: Scissors },
  { value: "automotive", label: "Garage auto", icon: Car },
  { value: "hotel", label: "Hôtel", icon: Building },
  { value: "other", label: "Autre", icon: MoreHorizontal }
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { refreshUserData, organization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    business_type: "",
    custom_business_type: "",
    address: "",
    phone: "",
    google_place_id: ""
  });

  // Redirect if already has organization
  React.useEffect(() => {
    if (organization) {
      navigate("/dashboard");
    }
  }, [organization, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTypeSelect = (value) => {
    setFormData({ ...formData, business_type: value, custom_business_type: "" });
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.business_type) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }
      if (formData.business_type === "other" && !formData.custom_business_type) {
        toast.error("Veuillez préciser votre type d'activité");
        return;
      }
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    // Validate Place ID
    if (!formData.google_place_id || !formData.google_place_id.trim()) {
      toast.error("Le Place ID Google est obligatoire");
      return;
    }

    // Basic Place ID validation (should start with ChI)
    if (!formData.google_place_id.startsWith("ChI")) {
      toast.error("Le Place ID semble invalide. Il doit commencer par 'ChI'");
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        business_type: formData.business_type === "other" 
          ? formData.custom_business_type 
          : formData.business_type,
        // Generate the google_business_url from Place ID
        google_business_url: `https://search.google.com/local/writereview?placeid=${formData.google_place_id.trim()}`
      };
      delete submitData.custom_business_type;
      
      await axios.post(`${API}/organizations`, submitData);
      await refreshUserData();
      toast.success("Établissement créé avec succès !");
      navigate("/select-plan");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                  s === step
                    ? "bg-violet-600 text-white"
                    : s < step
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {s < step ? "✓" : s}
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm">
            Étape {step} sur 3
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-violet-600" />
                </div>
                <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-2">
                  Votre établissement
                </h1>
                <p className="text-slate-500">
                  Commençons par les informations de base
                </p>
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">
                    Nom de l'établissement *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Ex: Restaurant Le Gourmet"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    data-testid="onboarding-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">Type d'activité *</Label>
                  <Select value={formData.business_type} onValueChange={handleTypeSelect}>
                    <SelectTrigger 
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="onboarding-type-select"
                    >
                      <SelectValue placeholder="Sélectionnez votre activité" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            <span>{type.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Custom business type input when "Autre" is selected - wrapped in stable container */}
                <div className="custom-type-wrapper" suppressHydrationWarning>
                  {formData.business_type === "other" ? (
                    <div className="space-y-2 animate-fade-in">
                      <Label htmlFor="custom_business_type" className="text-slate-700">
                        Précisez votre activité *
                      </Label>
                      <Input
                        id="custom_business_type"
                        name="custom_business_type"
                        type="text"
                        placeholder="Ex: Agence immobilière, Cabinet d'avocat..."
                        value={formData.custom_business_type}
                        onChange={handleChange}
                        required
                        className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                        data-testid="onboarding-custom-type-input"
                        autoComplete="off"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Address & Contact */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-violet-600" />
                </div>
                <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-2">
                  Coordonnées
                </h1>
                <p className="text-slate-500">
                  Ces informations sont optionnelles
                </p>
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-slate-700">Adresse</Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
                    value={formData.address}
                    onChange={handleChange}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    data-testid="onboarding-address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Téléphone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="Ex: 01 23 45 67 89"
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    data-testid="onboarding-phone-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Google Place ID */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <LinkIcon className="w-8 h-8 text-violet-600" />
                </div>
                <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-2">
                  Google Place ID
                </h1>
                <p className="text-slate-500">
                  Obligatoire pour rediriger vos clients vers votre fiche Google
                </p>
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="google_place_id" className="text-slate-700">
                    Place ID Google <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="google_place_id"
                    name="google_place_id"
                    type="text"
                    placeholder="Ex: ChIJN1t_tDeuEmsRUsoyG83frY4"
                    value={formData.google_place_id}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                    data-testid="onboarding-place-id-input"
                  />
                  <a 
                    href="https://developers.google.com/maps/documentation/places/web-service/place-id#find-id"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-violet-600 hover:text-violet-700 hover:underline mt-1"
                  >
                    <LinkIcon className="w-3 h-3" />
                    Comment trouver mon Place ID ?
                  </a>
                </div>
                
                <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                  <p className="text-sm text-violet-800 font-medium mb-2">Comment obtenir votre Place ID :</p>
                  <ol className="text-sm text-violet-700 space-y-2 list-decimal list-inside">
                    <li>
                      Allez sur{" "}
                      <a 
                        href="https://developers.google.com/maps/documentation/places/web-service/place-id#find-id"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-violet-600 underline font-medium"
                      >
                        Google Place ID Finder
                      </a>
                    </li>
                    <li>Cherchez votre établissement par nom et adresse</li>
                    <li>Copiez le <strong>Place ID</strong> qui commence par "ChI..."</li>
                  </ol>
                </div>

                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Important :</strong> Le Place ID est indispensable pour générer votre QR code 
                    et rediriger automatiquement vos clients satisfaits vers votre fiche Google.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between mt-8">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="rounded-full"
              >
                Retour
              </Button>
            )}
            <div className="flex-1" />
            {step < 3 ? (
              <Button
                onClick={handleNext}
                className="rounded-full bg-slate-900 hover:bg-slate-800"
                data-testid="onboarding-next-button"
              >
                Continuer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90"
                data-testid="onboarding-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    Créer mon établissement
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
